import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useNotification } from '../components/Notification';
import { getCurrencySymbol } from '../utils/currency';
import './Repairs.css';

function CreateServiceTemplate({ tenantId }) {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [hourlyRates, setHourlyRates] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [locations, setLocations] = useState([]);
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [showCreateInventoryModal, setShowCreateInventoryModal] = useState(false);
  const [selectedInventoryItem, setSelectedInventoryItem] = useState(null);
  const [withdrawQuantity, setWithdrawQuantity] = useState(1);
  const [inventoryFormData, setInventoryFormData] = useState({
    name: '',
    description: '',
    category: '',
    quantity: 1,
    unit: 'Stück',
    locationId: '',
    purchasePrice: 0,
    sellingPrice: 0,
    currentValue: 0,
    partNumber: ''
  });
  const [tenant, setTenant] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    materials: [{ description: '', amount: 0, quantity: 1, inventoryId: null }],
    labor: [{ hours: 0, hourlyRateId: '', description: '' }]
  });

  useEffect(() => {
    if (tenantId) {
      loadData();
    }
  }, [tenantId]);

  const loadData = async () => {
    await Promise.all([
      loadTenant(),
      loadHourlyRates(),
      loadInventory(),
      loadLocations()
    ]);
  };

  const loadTenant = async () => {
    try {
      const data = await api.get('/tenants', null);
      const currentTenant = data.find(t => t.id === tenantId) || data[0];
      setTenant(currentTenant);
    } catch (error) {
      console.error('Error loading tenant:', error);
    }
  };

  const getCurrency = () => {
    return getCurrencySymbol(tenant?.taxSystem || 'DE');
  };

  const loadHourlyRates = async () => {
    try {
      const data = await api.get('/hourly-rates', tenantId);
      setHourlyRates(data);
    } catch (error) {
      console.error('Error loading hourly rates:', error);
    }
  };

  const loadInventory = async () => {
    try {
      const data = await api.get('/inventory', tenantId);
      setInventory(data.filter(item => (item.quantity || 0) > 0));
    } catch (error) {
      console.error('Error loading inventory:', error);
    }
  };

  const loadLocations = async () => {
    try {
      const data = await api.get('/locations', tenantId);
      setLocations(data);
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const calculateLaborAmount = (lab) => {
    if (lab.hours && lab.hourlyRateId) {
      const rate = hourlyRates.find(r => r.id === lab.hourlyRateId);
      if (rate) {
        return lab.hours * rate.rate;
      }
    }
    return 0;
  };

  const handleMaterialChange = (index, field, value) => {
    const newMaterials = [...formData.materials];
    if (field === 'amount' || field === 'quantity') {
      newMaterials[index] = { ...newMaterials[index], [field]: value === '' ? '' : value };
    } else {
      newMaterials[index] = { ...newMaterials[index], [field]: value };
    }
    setFormData({ ...formData, materials: newMaterials });
  };

  const handleMaterialBlur = (index, field) => {
    const newMaterials = [...formData.materials];
    if (field === 'amount' || field === 'quantity') {
      const val = newMaterials[index][field] === '' ? 0 : parseFloat(newMaterials[index][field]) || 0;
      newMaterials[index] = { ...newMaterials[index], [field]: val };
      setFormData({ ...formData, materials: newMaterials });
    }
  };

  const handleLaborChange = (index, field, value) => {
    const newLabor = [...formData.labor];
    if (field === 'hours') {
      newLabor[index] = { ...newLabor[index], [field]: value === '' ? '' : value };
    } else {
      newLabor[index] = { ...newLabor[index], [field]: value };
    }
    setFormData({ ...formData, labor: newLabor });
  };

  const handleLaborBlur = (index, field) => {
    const newLabor = [...formData.labor];
    if (field === 'hours') {
      const val = newLabor[index][field] === '' ? 0 : parseFloat(newLabor[index][field]) || 0;
      newLabor[index] = { ...newLabor[index], [field]: val };
      setFormData({ ...formData, labor: newLabor });
    }
  };

  const addMaterial = () => {
    const currentMaterials = formData.materials.filter(mat => 
      mat.description && mat.description.trim() !== ''
    );
    setFormData({
      ...formData,
      materials: [...currentMaterials, { description: '', amount: 0, quantity: 1, inventoryId: null }]
    });
  };

  const removeMaterial = (index) => {
    const newMaterials = formData.materials.filter((_, i) => i !== index);
    if (newMaterials.length === 0) {
      newMaterials.push({ description: '', amount: 0, quantity: 1, inventoryId: null });
    }
    setFormData({ ...formData, materials: newMaterials });
  };

  const addLabor = () => {
    setFormData({
      ...formData,
      labor: [...formData.labor, { hours: 0, hourlyRateId: '', description: '' }]
    });
  };

  const removeLabor = (index) => {
    const newLabor = formData.labor.filter((_, i) => i !== index);
    if (newLabor.length === 0) {
      newLabor.push({ hours: 0, hourlyRateId: '', description: '' });
    }
    setFormData({ ...formData, labor: newLabor });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        name: formData.name,
        description: formData.description || '',
        materials: formData.materials.map(mat => ({
          description: mat.description || '',
          amount: mat.amount === '' ? 0 : parseFloat(mat.amount) || 0,
          quantity: mat.quantity === '' ? 1 : parseFloat(mat.quantity) || 1,
          inventoryId: mat.inventoryId || null
        })),
        labor: formData.labor.map(lab => ({
          hours: lab.hours === '' ? 0 : parseFloat(lab.hours) || 0,
          hourlyRateId: lab.hourlyRateId || '',
          description: lab.description || ''
        }))
      };
      
      await api.post('/service-templates', submitData, tenantId);
      navigate('/service-templates');
    } catch (error) {
      console.error('Error saving template:', error);
      showNotification('Fehler beim Speichern des Standardauftrags: ' + (error.response?.data?.error || error.message || 'Unbekannter Fehler', 'error'));
    }
  };

  const materialsAmount = formData.materials.reduce((sum, mat) => sum + ((mat.amount || 0) * (mat.quantity || 1)), 0);
  const laborAmount = formData.labor.reduce((sum, lab) => sum + calculateLaborAmount(lab), 0);
  const totalAmount = materialsAmount + laborAmount;

  return (
    <div className="repairs">
      <div className="page-header">
        <h1>Neuer Standardauftrag</h1>
        <button type="button" onClick={() => navigate('/service-templates')} className="btn-secondary">
          Zurück
        </button>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>Materialien</label>
            {formData.materials.map((material, index) => (
              <div key={index} className="item-row">
                <input
                  type="text"
                  placeholder="Beschreibung"
                  value={material.description}
                  onChange={(e) => handleMaterialChange(index, 'description', e.target.value)}
                  className="item-description"
                />
                <div className="item-quantity-wrapper">
                  <input
                    type="number"
                    placeholder="Menge"
                    value={material.quantity}
                    onChange={(e) => handleMaterialChange(index, 'quantity', e.target.value)}
                    onBlur={() => handleMaterialBlur(index, 'quantity')}
                    className="item-quantity"
                    min="0"
                    step="0.01"
                  />
                  <span className="input-unit">Stück</span>
                </div>
                <div className="item-amount-wrapper">
                  <input
                    type="number"
                    placeholder="Betrag"
                    value={material.amount}
                    onChange={(e) => handleMaterialChange(index, 'amount', e.target.value)}
                    onBlur={() => handleMaterialBlur(index, 'amount')}
                    className="item-amount"
                    min="0"
                    step="0.01"
                  />
                  <span className="input-unit">{getCurrency()}</span>
                </div>
                <button
                  type="button"
                  onClick={() => removeMaterial(index)}
                  className="btn-small btn-danger"
                >
                  Entfernen
                </button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" onClick={addMaterial} className="btn-secondary btn-small">
                Material hinzufügen
              </button>
              <button 
                type="button" 
                onClick={() => setShowInventoryModal(true)} 
                className="btn-secondary btn-small"
              >
                Aus Inventar entnehmen
              </button>
              <button 
                type="button" 
                onClick={() => setShowCreateInventoryModal(true)} 
                className="btn-secondary btn-small"
              >
                Neues Inventar erfassen
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>Arbeitseinsatz</label>
            {formData.labor.map((lab, index) => (
              <div key={index} className="item-row-extended">
                <div className="item-hours-wrapper">
                  <input
                    type="number"
                    placeholder="Stunden"
                    value={lab.hours}
                    onChange={(e) => handleLaborChange(index, 'hours', e.target.value)}
                    onBlur={() => handleLaborBlur(index, 'hours')}
                    className="item-hours"
                    min="0"
                    step="0.1"
                  />
                  <span className="input-unit">h</span>
                </div>
                <select
                  value={lab.hourlyRateId || ''}
                  onChange={(e) => handleLaborChange(index, 'hourlyRateId', e.target.value)}
                  className="item-hourly-rate"
                  style={{ flex: 1 }}
                >
                  <option value="">Stundensatz wählen</option>
                  {hourlyRates.map(rate => (
                    <option key={rate.id} value={rate.id}>
                      {rate.name} ({rate.rate.toFixed(2)} {getCurrency()}/h)
                    </option>
                  ))}
                </select>
                {lab.hours && lab.hourlyRateId && (
                  <div className="item-amount-wrapper">
                    <span className="input-unit" style={{ position: 'static', padding: '0.5rem' }}>
                      {calculateLaborAmount(lab).toFixed(2)} {getCurrency()}
                    </span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removeLabor(index)}
                  className="btn-small btn-danger"
                >
                  Entfernen
                </button>
              </div>
            ))}
            <button type="button" onClick={addLabor} className="btn-secondary btn-small">
              Arbeitseinsatz hinzufügen
            </button>
          </div>

          <div className="total-amount">
            <strong>Gesamtbetrag: {totalAmount.toFixed(2)} {getCurrency()}</strong>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary">
              Erstellen
            </button>
            <button
              type="button"
              onClick={() => navigate('/service-templates')}
              className="btn-secondary"
            >
              Abbrechen
            </button>
          </div>
        </form>
      </div>

      {showInventoryModal && (
        <div className="modal">
          <div className="modal-content">
            <h2>Aus Inventar entnehmen</h2>
            <div className="form-group">
              <label>Inventar-Item</label>
              <select
                value={selectedInventoryItem || ''}
                onChange={(e) => {
                  const item = inventory.find(i => i.id === e.target.value);
                  setSelectedInventoryItem(item ? e.target.value : null);
                  if (item) {
                    setWithdrawQuantity(1);
                  }
                }}
              >
                <option value="">Bitte wählen</option>
                {inventory.map(item => (
                  <option key={item.id} value={item.id}>
                    {item.name} (Verfügbar: {item.quantity} {item.unit}, Verkaufspreis: {item.sellingPrice?.toFixed(2) || 0} {getCurrency()})
                  </option>
                ))}
              </select>
            </div>
            {selectedInventoryItem && (
              <>
                <div className="form-group">
                  <label>Menge</label>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    max={inventory.find(i => i.id === selectedInventoryItem)?.quantity || 0}
                    value={withdrawQuantity}
                    onChange={(e) => setWithdrawQuantity(parseFloat(e.target.value) || 0)}
                  />
                  <span style={{ marginLeft: '0.5rem', color: '#666' }}>
                    {inventory.find(i => i.id === selectedInventoryItem)?.unit}
                  </span>
                </div>
                <div className="form-actions">
                  <button
                    type="button"
                    onClick={() => {
                      const item = inventory.find(i => i.id === selectedInventoryItem);
                      if (!item) return;
                      
                      const newMaterial = {
                        description: item.name,
                        amount: item.sellingPrice || 0,
                        quantity: withdrawQuantity,
                        inventoryId: item.id
                      };
                      
                      const currentMaterials = formData.materials.filter(mat => 
                        mat.description && mat.description.trim() !== ''
                      );
                      
                      setFormData({
                        ...formData,
                        materials: [...currentMaterials, newMaterial]
                      });
                      
                      setShowInventoryModal(false);
                      setSelectedInventoryItem(null);
                      setWithdrawQuantity(1);
                    }}
                    className="btn-primary"
                    disabled={!selectedInventoryItem || withdrawQuantity <= 0}
                  >
                    Hinzufügen
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowInventoryModal(false);
                      setSelectedInventoryItem(null);
                      setWithdrawQuantity(1);
                    }}
                    className="btn-secondary"
                  >
                    Abbrechen
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CreateServiceTemplate;

